import { useState } from "react";
import BusinessSummary from "../../components/BusinessSummary";
import BusinessForm from "../../components/BusinessForm";
import LeadCaptureModal from "../../components/LeadCaptureModal";
import { runBusinessAssessment } from "../../api/diagnosticsApi";
import { Loader2, CheckCircle2, Zap, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";

export default function BusinessAssessmentTool() {
  const [report, setReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresLeadCapture, setRequiresLeadCapture] = useState(false);
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, string>>({});
  const [, setLeadCaptureId] = useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { token } = useAuth();
  const isAuthenticated = !!token;
  const pageWrapperClass = isAuthenticated
  ? "min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0"
  : "min-h-screen px-4 sm:px-6 lg:px-8 text-slate-100";

  async function runDiagnostic(answers: Record<string, string>) {
    setIsLoading(true);
    setError(null);
    setPendingAnswers(answers);
    try {
      const data = await runBusinessAssessment({ answers });
      if (data.requiresLeadCapture) {
        setRequiresLeadCapture(true);
        setReport(null);
        setLeadCaptured(false);
      } else {
        setReport(data.report ?? data);
        setRequiresLeadCapture(false);
        setLeadCaptured(false);
        setLeadCaptureId(data.leadCaptureId ?? null);
      }
    } catch (err) {
      console.error("Error running business diagnostic:", err);
      setError(err instanceof Error ? err.message : "Failed to run diagnostics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleLeadCaptureSubmit(data: { name: string; company: string; email: string }) {
    setIsLoading(true);
    try {
      const response = await runBusinessAssessment({ answers: pendingAnswers, name: data.name, email: data.email, companyName: data.company });
      if (response.report) {
        setReport(response.report);
        setRequiresLeadCapture(false);
        setLeadCaptured(true);
        setLeadCaptureId(response.leadCaptureId ?? null);
        setCapturedEmail(data.email);
      } else {
        throw new Error("No report was returned after lead capture.");
      }
    } catch (err) {
      console.error("Error submitting lead capture:", err);
      setError(err instanceof Error ? err.message : "Failed to submit lead capture. Please try again.");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  function resetState() {
    setRequiresLeadCapture(false);
    setLeadCaptured(false);
    setCapturedEmail(null);
    setLeadCaptureId(null);
    setReport(null);
    setError(null);
    setPendingAnswers({});
  }

  return (
    <div className={pageWrapperClass}>
      <LeadCaptureModal
        isOpen={requiresLeadCapture}
        onClose={resetState}
        onSubmit={handleLeadCaptureSubmit}
        loading={isLoading}
        websiteUrl="your business assessment"
        title="Get Your Business Assessment Report"
        description="We need a few details so we can send your assessment report and follow up with tailored recommendations."
        companyLabel="Company"
      />

      {!isAuthenticated ? (
        <header className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-slate-950 text-white border-b border-slate-800 shadow-lg mb-12">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">

            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                <span className="text-sky-400 font-bold">L</span>
              </div>

              <h1 className="font-bold text-xl tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Strivre Diagnostics
              </h1>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/diagnostics/website-tool"
                className="text-slate-300 hover:text-sky-400 transition-colors"
              >
                Web Diagnostics
              </Link>

              <Link
                to="/diagnostics/marketing-tool"
                className="text-slate-300 hover:text-sky-400 transition-colors"
              >
                Marketing Diagnostics
              </Link>

              <Link
                to="/diagnostics/business-assessment"
                className="text-sky-400 font-medium"
              >
                Business Diagnostics
              </Link>
            </nav>

            <div className="flex items-center gap-3">
                <button
                  className="btn-primary btn-md shrink-0"
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>

                <button
                  className="btn-secondary btn-md shrink-0"
                  onClick={() => navigate("/register")}
                >
                  Sign Up
                </button>
            </div>
          </div>
        </header>
      ) : (
        <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Zap className="text-indigo-600" size={24} />
              {report && !isLoading
                ? "Assessment Results"
                : "Business Assessment"}
            </h1>

            <p
              className={`text-slate-500 text-sm mt-1 ${
                !report || isLoading ? "text-center" : "text-left"
              }`}
            >
              {report && !isLoading
                ? "Review your personalized business health assessment below."
                : "Answer a few questions to assess your business operations, marketing, technology, and growth readiness."}
            </p>
          </div>
        </header>
      )}

      {!report && !isLoading && !requiresLeadCapture && (
        <div className="w-full">
          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <BusinessForm onSubmit={runDiagnostic} />
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 animate-pulse">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
          <p className="text-slate-500 font-medium text-lg">Analyzing your business structure...</p>
        </div>
      )}

      {report && !isLoading && !requiresLeadCapture && (
        <>
          {!isAuthenticated && leadCaptured ? (
            <div className="max-w-2xl mx-auto mb-12 animate-fade-in">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl border-2 border-emerald-200 p-12 text-center shadow-lg">

                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center">
                      <CheckCircle2 size={40} className="text-white" />
                    </div>

                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-4 border-white">
                      <Mail size={16} className="text-white" />
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Assessment Sent to Your Email!
                </h2>

                <p className="text-slate-700 text-lg mb-2">
                  We've sent your detailed business assessment to:
                </p>

                <p className="text-emerald-700 font-semibold text-lg mb-8 break-all">
                  {capturedEmail}
                </p>

                <div className="space-y-3 text-left text-slate-700 mb-8 max-w-md mx-auto">

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      Check your inbox for your complete business health report
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      The report contains recommendations to improve your operations,
                      marketing, digital strategy and technology
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      Our consultants may contact you to discuss growth opportunities
                    </p>
                  </div>

                </div>

                <p className="text-sm text-slate-600 mb-6">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    onClick={resetState}
                    className="text-emerald-700 hover:text-emerald-800 font-semibold underline"
                  >
                    try another email
                  </button>
                </p>

                <button
                  onClick={resetState}
                  className="inline-block px-8 py-3 bg-emerald-600 text-white font-bold rounded-full hover:bg-emerald-700 transition-colors"
                >
                  Run Another Assessment
                </button>

              </div>
            </div>
          ) : (
            <div className="mt-8">
              <BusinessSummary
                overallPercentage={report.overallPercentage}
                visualIndicator={report.visualIndicator || report.grade}
                categoryScores={report.categoryScores}
                report={report.summaryReport}
                recommendations={report.recommendations}
                services={report.ctaServices}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}